/**
 * Centralized labels for headings and subheadings.
 * Use getLabel(lang, key) to get the string for the current language.
 */

export type LabelKey = keyof typeof LABELS.en;

export const LABELS = {
  en: {
    // Navigation & header
    TELUGU: "TELUGU",
    ENGLISH: "ENGLISH",
    E_PAPER: "E-PAPER",
    LATEST_NEWS: "Latest News",
    REGISTER: "Register",
    MAGAZINES: "MAGAZINES",
    SEARCH: "Search",
    SEARCH_PLACEHOLDER: "Search here...",
    PLEASE_FILL_THIS_FIELD: "Please fill in this field.",
    MOST_SEARCHED_NEWS: "Most Searched News",
    NO_NEWS_FOUND: "No News Found",
    DEVELOPED_BY: "Developed by",
    LIVE_NEWS: "Live News",
    PRINT_EDITION: "Print Edition",
    BY: "BY",
    READ_MORE: "Read More",
    HOME: "Home",
    REPORTER: "Reporter",
    ME: "Me",
    STORIES: "Stories",
    CATEGORIES: "Categories",
    ALL_NEWS: "Latest News",
    ARTICLES: "Articles",
    INTERVIEWS: "Interviews",
    VISUAL_STORIES: "Visual Stories",
    PHOTO_STORIES: "Photo Stories",
    // Section headings
    TAGS: "Tags",
    RELATED_NEWS: "Related News",
    RECOMMENDED: "Recommended",
    RECOMMENDED_FOR_YOU: "Recommended for you",
    DONT_MISS_IT: "Don't Miss It",
    // Page titles (metadata & section headers)
    SEARCH_PAGE: "Search",
    REGISTER_PAGE: "Register",
    LOGIN_PAGE: "Login",
    ABOUT_US: "About Us",
    TERMS_OF_USE: "Terms of Use",
    PRIVACY_POLICY: "Privacy Policy",
    INTERVIEWS_PAGE: "Interviews",
    ARTICLES_PAGE: "Articles",
    LATEST_NEWS_PAGE: "Latest News",
    SAVED_NEWS: "Saved News",
    PROFILE: "Profile",
    DELETE_ACCOUNT: "Delete Account",
    FORGOT_PASSWORD: "Forgot Password",
    RESET_PASSWORD: "Reset Password",
    VERIFY_OTP: "Verify OTP",
    PODCASTS: "Podcasts",
    VISUAL_STORIES_PAGE: "Visual Stories",
    PHOTO_STORIES_PAGE: "Photo Stories",
    NEWS_IN_PICS: "News in Pics",
    SHORTS_PAGE: "Shorts",
    OFFLINE: "Offline",
    NEWS: "News",
    // User menu
    MY_PROFILE: "My Profile",
    SAVED_NEWS_MENU: "Saved News",
    DELETE_ACCOUNT_MENU: "Delete Account",
    LOGOUT: "Logout",
    LOGOUT_SUCCESS: "Logged out successfully",
    // Block titles (from CMS)
    VIDEOS: "Videos",
    POPULAR_NEWS: "Popular News",
    RECENT_VIDEOS: "Recent Videos",
    GLOBAL: "Global",
    GLOBAL_PROGRESSIVE_MOVEMENTS: "Global Progressive Movements",
    WEBSITE_SPECIAL: "Special",
    SPECIALS: "Specials",
    FESTIVALS: "Festivals",
    THE_LATEST: "The Latest",
    ARTICLES_AND_PERIODICALS: "Articles & Periodicals",
    INTERVIEW: "Interview",
    INTERVIEWEE: "Interviewee",
    VIEW_MORE: "View More",
    LOAD_MORE: "Load More",
    LOAD_MORE_COMMENTS: "Load More Comments",
    // Menu / category headings
    LITERATURE: "Literature",
    EXAM: "Exam",
    NATIONAL: "National",
    INDIA: "India",
    EDITORIAL: "Editorial",
    MAIN_ARTICLE: "Main Article",
    VIDEO_NEWS: "Video News",
    // Auth (login / register)
    DONT_HAVE_ACCOUNT: "Don't have an account?",
    SIGN_UP: "Sign up!",
    WELCOME_BACK: "Welcome Back",
    LOGIN_INTO_ACCOUNT: "Login into your account",
    OR_CONTINUE_WITH: "Or continue with",
    LOGIN_WITH_EMAIL: "Login with Email",
    LOGIN_WITH_PHONE: "Login with Phone",
    ENTER_EMAIL: "Enter Email",
    PASSWORD: "Password",
    REMEMBER_ME: "Remember me",
    LOG_IN: "Log In",
    HAVE_ACCOUNT: "Have an account?",
    SIGN_IN: "Sign in!",
    GET_STARTED_WITH: "Get Started With",
    GETTING_STARTED_EASY: "Getting started is easy",
    FULL_NAME: "Full Name",
    ENTER_PHONE: "Enter Phone Number",
    CONFIRM_PASSWORD: "Confirm Password",
    REGISTER_BUTTON: "Register",
    CREATE_ACCOUNT: "Create Account",
    TERMS_AGREE: "By continuing you indicate that you read and agreed to the Terms of Use",
    LOGIN_SUCCESSFUL: "Login successful!",
    REGISTRATION_SUCCESSFUL: "Registration successful!",
    INVALID_CREDENTIALS: "Invalid credentials!",
    TRY_AGAIN: "Something went wrong, Try again",
    SOCIAL_AUTH_ACCOUNT_INACTIVE: "This account is inactive. Please contact support.",
    SOCIAL_AUTH_ACCOUNT_SUSPENDED: "This account is suspended. Please contact support.",
    SOCIAL_AUTH_ACCOUNT_DELETED: "This account has been deleted. Please register again.",
    SOCIAL_AUTH_STAFF_LOGIN_BLOCKED:
      "This email cannot sign in here. Please use the admin panel.",
    REQUIRED: "*Required",
    MIN_8_CHARS: "Must be at least 8 characters",
    PASSWORD_MUST_MATCH: "Password must be same",
    VALID_PHONE_10: "Must be a valid 10-digit phone number",
    VALID_EMAIL: "Please enter a valid email address",
    FORGOT_PASSWORD_INSTRUCTION: "Please enter your email to reset the password",
    SEND_ME_LINK: "Send Me Link",
    RESET_LINK_SENT_SUCCESS: "Reset link sent to your email.",
    RESET_EMAIL_INSTRUCTION:
      "An email has been sent to your email address with instructions to reset your password.",
    NO_BOOKMARKS_FOUND: "No bookmarks found",
    USER_ALREADY_EXISTS: "A user already exists.",
    PASSWORD_COMPLEXITY:
      "Password must be at least 8 characters and include uppercase, lowercase, a number and a special character.",
    PASSWORD_RULE_LOWERCASE: "At least one lowercase letter.",
    PASSWORD_RULE_UPPERCASE: "At least one uppercase letter.",
    PASSWORD_RULE_SPECIAL: "At least one special character.",
    PASSWORD_RULE_DIGIT: "At least one digit.",
    PASSWORD_RULE_MIN_8: "At least 8 characters long.",
    REGISTRATION_SERVICE_UNAVAILABLE: "Registration service is unavailable. Please try again later.",
    REGISTRATION_FAILED: "Registration failed. Please check your connection and try again.",
    CANNOT_CONNECT_SERVER: "Cannot connect to server. Make sure the backend is running and reachable.",
    PHONE_OTP_INVALID: "Please enter a valid 6-digit OTP.",
    GO_TO_LOGIN: "Go to Login Page",
    PASSWORD_CHANGED: "Congratulations! Your password has been changed. Click continue to login",
    // Profile page
    CHANGE_PROFILE: "Change Profile",
    UPLOAD_FILE_INSTRUCTION: "Please upload a file (JPG, JPEG, or PNG) less than 1 MB",
    REMOVE: "Remove",
    EMAIL: "Email",
    PHONE_NUMBER: "Phone Number",
    COUNTRY: "Country",
    STATE: "State",
    CITY: "City",
    UPDATE: "Update",
    PROFILE_UPDATED_SUCCESS: "Profile updated successfully",
    EX_JOHN_DEO: "Ex: John Deo",
    ENTER_EMAIL_PLACEHOLDER: "Enter Email",
    ENTER_PHONE_PLACEHOLDER: "Enter phone number",
    EX_INDIA: "Ex: India",
    EX_KERALA: "Ex: Kerala",
    EX_CALICUT: "Ex: Calicut",
    INVALID_PHONE: "Invalid phone number",
    PHONE_NUMBER_EXISTS: "This phone number is already registered. Please use a different number.",
    LOADING: "Loading...",
    UNABLE_TO_LOAD_BOOKMARKS: "Unable to load bookmarks. Please try again later.",
    DELETE_ACCOUNT_CONFIRM: "Are you sure you want to delete?",
    ACCOUNT_DELETED_SUCCESS: "Account deleted successfully",
    ACCOUNT_DELETION_FAILED: "Account deletion failed!",
    DELETE_ACCOUNT_WARNING:
      "Warning: Deleting your account is a permanent action and cannot be undone. All your data will be lost and cannot be recovered. Please proceed with caution.",
    DELETE: "Delete",
    CANCEL: "Cancel",
    CONFIRM: "Confirm",
    PHOTO: "Photo",
    PHOTOGRAPHER: "Photographer",
    PUBLISHED_ON: "Published on",
    MIN_READ: "min read",
    NEWS_DESK: "News Desk",
    BOOKMARK: "Bookmark",
    LIKES: "Likes",
    BLOCK_NOT_AVAILABLE: "Block data not available",
    NO_ARTICLES: "No articles found for this subcategory",
    CATEGORY: "Category",
    SUBCATEGORY: "SubCategory",
    NEXT_STORY: "NEXT STORY",
    CLICK_TO_VIEW: "Click to view",
  },
  te: {
    // Navigation & header
    TELUGU: "తెలుగు",
    ENGLISH: "ఇంగ్లీష్",
    E_PAPER: "E-PAPER",
    LATEST_NEWS: "తాజా వార్తలు",
    REGISTER: "నమోదు",
    MAGAZINES: "మ్యాగజైన్‌లు",
    SEARCH: "వెతకండి",
    SEARCH_PLACEHOLDER: "ఇక్కడ వెతకండి...",
    PLEASE_FILL_THIS_FIELD: "దయచేసి ఈ ఫీల్డ్‌ను పూరించండి.",
    MOST_SEARCHED_NEWS: "అత్యంత వెతకబడిన వార్తలు",
    NO_NEWS_FOUND: "వార్తలు కనుగొనబడలేదు",
    DEVELOPED_BY: "అభివృద్ధి చేసినవారు",
    LIVE_NEWS: "లైవ్ వార్తలు",
    PRINT_EDITION: "ప్రింట్ ఎడిషన్",
    BY: "రచన",
    READ_MORE: "మరింత చదవండి",
    HOME: "హోమ్",
    REPORTER: "రిపోర్టర్",
    ME: "నేను",
    STORIES: "కథలు",
    CATEGORIES: "వర్గాలు",
    ALL_NEWS: "తాజా వార్తలు",
    ARTICLES: "ఆర్టికల్స్",
    INTERVIEWS: "ఇంటర్వ్యూలు",
    VISUAL_STORIES: "విజువల్ స్టోరీలు",
    PHOTO_STORIES: "ఫోటో స్టోరీలు",
    // Section headings
    TAGS: "ట్యాగ్‌లు",
    RELATED_NEWS: "సంబంధిత వార్తలు",
    RECOMMENDED: "సిఫార్సు చేయబడింది",
    RECOMMENDED_FOR_YOU: "మీకు సిఫార్సు చేయబడింది",
    DONT_MISS_IT: "వదిలిపెట్టవద్దు",
    // Page titles
    SEARCH_PAGE: "వెతకండి",
    REGISTER_PAGE: "నమోదు",
    LOGIN_PAGE: "లాగిన్",
    ABOUT_US: "మా గురించి",
    TERMS_OF_USE: "వాడక నిబంధనలు",
    PRIVACY_POLICY: "గోప్యతా విధానం",
    INTERVIEWS_PAGE: "ఇంటర్వ్యూలు",
    ARTICLES_PAGE: "ఆర్టికల్స్",
    LATEST_NEWS_PAGE: "తాజా వార్తలు",
    SAVED_NEWS: "సేవ్ చేసిన వార్తలు",
    PROFILE: "ప్రొఫైల్",
    DELETE_ACCOUNT: "ఖాతా తొలగించు",
    FORGOT_PASSWORD: "పాస్‌వర్డ్ మర్చిపోయారా",
    RESET_PASSWORD: "పాస్‌వర్డ్ రీసెట్",
    VERIFY_OTP: "OTP ధృవీకరించండి",
    PODCASTS: "పాడ్‌కాస్ట్‌లు",
    VISUAL_STORIES_PAGE: "విజువల్ స్టోరీలు",
    PHOTO_STORIES_PAGE: "ఫోటో స్టోరీలు",
    NEWS_IN_PICS: "ఫోటోలలో వార్తలు",
    SHORTS_PAGE: "షార్ట్స్",
    OFFLINE: "ఆఫ్‌లైన్",
    NEWS: "వార్తలు",
    MY_PROFILE: "నా ప్రొఫైల్",
    SAVED_NEWS_MENU: "సేవ్ చేసిన వార్తలు",
    DELETE_ACCOUNT_MENU: "ఖాతా తొలగించు",
    LOGOUT: "లాగౌట్",
    LOGOUT_SUCCESS: "విజయవంతంగా లాగౌట్ అయ్యారు",
    VIDEOS: "వీడియోలు",
    POPULAR_NEWS: "జనాదరణ వార్తలు",
    RECENT_VIDEOS: "ఇటీవలి వీడియోలు",
    GLOBAL: "గ్లోబల్",
    GLOBAL_PROGRESSIVE_MOVEMENTS: "గ్లోబల్ ప్రోగ్రెసివ్ మూవ్‌మెంట్‌లు",
    WEBSITE_SPECIAL: "స్పెషల్",
    SPECIALS: "స్పెషల్స్",
    FESTIVALS: "పండుగలు",
    THE_LATEST: "తాజా",
    ARTICLES_AND_PERIODICALS: "ఆర్టికల్స్ మరియు పీరియాడికల్స్",
    INTERVIEW: "ఇంటర్వ్యూ",
    INTERVIEWEE: "ఇంటర్వ్యూదారుడు",
    VIEW_MORE: "మరిన్ని చూడండి",
    LOAD_MORE: "మరిన్ని లోడ్ చేయండి",
    LOAD_MORE_COMMENTS: "మరిన్ని కామెంట్‌లు",
    LITERATURE: "సాహిత్యం",
    EXAM: "పరీక్ష",
    NATIONAL: "జాతీయ",
    INDIA: "భారత్",
    EDITORIAL: "ఎడిటోరియల్",
    MAIN_ARTICLE: "ప్రధాన కథనం",
    VIDEO_NEWS: "వీడియో వార్తలు",
    // Auth (login / register)
    DONT_HAVE_ACCOUNT: "ఖాతా లేదా?",
    SIGN_UP: "నమోదు చేయండి!",
    WELCOME_BACK: "మళ్ళీ స్వాగతం",
    LOGIN_INTO_ACCOUNT: "మీ ఖాతాలోకి లాగిన్ అవండి",
    OR_CONTINUE_WITH: "లేదా కొనసాగించండి",
    LOGIN_WITH_EMAIL: "ఇమెయిల్ తో లాగిన్",
    LOGIN_WITH_PHONE: "ఫోన్ తో లాగిన్",
    ENTER_EMAIL: "ఇమెయిల్ నమోదు చేయండి",
    PASSWORD: "పాస్‌వర్డ్",
    REMEMBER_ME: "నన్ను గుర్తు పెట్టుకో",
    LOG_IN: "లాగిన్",
    HAVE_ACCOUNT: "ఖాతా ఉందా?",
    SIGN_IN: "సైన్ ఇన్!",
    GET_STARTED_WITH: "ప్రారంభించండి",
    GETTING_STARTED_EASY: "ప్రారంభించడం సులభం",
    FULL_NAME: "పూర్తి పేరు",
    ENTER_PHONE: "ఫోన్ నంబర్ నమోదు చేయండి",
    CONFIRM_PASSWORD: "పాస్‌వర్డ్ నిర్ధారించండి",
    REGISTER_BUTTON: "నమోదు",
    CREATE_ACCOUNT: "ఖాతా సృష్టించండి",
    TERMS_AGREE: "కొనసాగడం ద్వారా మీరు వాడక నిబంధనలను చదివి అంగీకరించారని సూచిస్తారు",
    LOGIN_SUCCESSFUL: "లాగిన్ విజయవంతమైంది!",
    REGISTRATION_SUCCESSFUL: "నమోదు విజయవంతమైంది!",
    INVALID_CREDENTIALS: "చెల్లని వివరాలు!",
    TRY_AGAIN: "ఏదో తప్పు జరిగింది, మళ్ళీ ప్రయత్నించండి",
    SOCIAL_AUTH_ACCOUNT_INACTIVE: "ఈ ఖాతా నిష్క్రియంగా ఉంది. దయచేసి సపోర్ట్‌ను సంప్రదించండి.",
    SOCIAL_AUTH_ACCOUNT_SUSPENDED: "ఈ ఖాతా సస్పెండ్ చేయబడింది. దయచేసి సపోర్ట్‌ను సంప్రదించండి.",
    SOCIAL_AUTH_ACCOUNT_DELETED: "ఈ ఖాతా తొలగించబడింది. దయచేసి మళ్ళీ నమోదు చేయండి.",
    SOCIAL_AUTH_STAFF_LOGIN_BLOCKED:
      "ఈ ఇమెయిల్ ఇక్కడ సైన్ ఇన్ చేయలేరు. దయచేసి అడ్మిన్ ప్యానెల్ ఉపయోగించండి.",
    REQUIRED: "*అవసరం",
    MIN_8_CHARS: "కనీసం 8 అక్షరాలు ఉండాలి",
    PASSWORD_MUST_MATCH: "పాస్‌వర్డ్ ఒకేలా ఉండాలి",
    VALID_PHONE_10: "చెల్లుబాటు అయ్యే 10 అంకెల ఫోన్ నంబర్ నమోదు చేయండి",
    VALID_EMAIL: "దయచేసి చెల్లుబాటు అయ్యే ఇమెయిల్ చిరునామాను నమోదు చేయండి",
    FORGOT_PASSWORD_INSTRUCTION: "పాస్‌వర్డ్ రీసెట్ చేయడానికి మీ ఇమెయిల్ నమోదు చేయండి",
    SEND_ME_LINK: "లింక్ పంపండి",
    RESET_LINK_SENT_SUCCESS: "మీ ఇమెయిల్‌కు రీసెట్ లింక్ పంపబడింది.",
    RESET_EMAIL_INSTRUCTION:
      "మీ ఇమెయిల్ చిరునామాకు పాస్‌వర్డ్ రీసెట్ చేయడానికి సూచనలతో ఇమెయిల్ పంపబడింది.",
    NO_BOOKMARKS_FOUND: "బుక్‌మార్క్‌లు ఏవీ కనుగొనబడలేదు",
    USER_ALREADY_EXISTS: "ఈ వినియోగదారు ఇప్పటికే ఉన్నారు.",
    PASSWORD_COMPLEXITY:
      "పాస్‌వర్డ్ కనీసం 8 అక్షరాలు ఉండాలి; పెద్ద అక్షరం, చిన్న అక్షరం, సంఖ్య మరియు ప్రత్యేక చిహ్నం ఉండాలి.",
    PASSWORD_RULE_LOWERCASE: "కనీసం ఒక చిన్న అక్షరం ఉండాలి.",
    PASSWORD_RULE_UPPERCASE: "కనీసం ఒక పెద్ద అక్షరం ఉండాలి.",
    PASSWORD_RULE_SPECIAL: "కనీసం ఒక ప్రత్యేక చిహ్నం ఉండాలి.",
    PASSWORD_RULE_DIGIT: "కనీసం ఒక సంఖ్య ఉండాలి.",
    PASSWORD_RULE_MIN_8: "కనీసం 8 అక్షరాలు ఉండాలి.",
    REGISTRATION_SERVICE_UNAVAILABLE:
      "నమోదు సేవ అందుబాటులో లేదు. దయచేసి తర్వాత మళ్ళీ ప్రయత్నించండి.",
    REGISTRATION_FAILED: "నమోదు విఫలమైంది. దయచేసి కనెక్షన్ తనిఖీ చేసి మళ్ళీ ప్రయత్నించండి.",
    CANNOT_CONNECT_SERVER:
      "సర్వర్‌కు కనెక్ట్ కాలేము. బ్యాకెండ్ నడుస్తోందో లేదో తనిఖీ చేయండి.",
    PHONE_OTP_INVALID: "దయచేసి చెల్లుబాటు అయ్యే 6 అంకెల OTP నమోదు చేయండి.",
    GO_TO_LOGIN: "లాగిన్ పేజీకి వెళ్ళండి",
    PASSWORD_CHANGED:
      "అభినందనలు! మీ పాస్‌వర్డ్ మార్చబడింది. లాగిన్ చేయడానికి కొనసాగించండి క్లిక్ చేయండి",
    // Profile page
    CHANGE_PROFILE: "ప్రొఫైల్ మార్చండి",
    UPLOAD_FILE_INSTRUCTION: "దయచేసి 1 MB కంటే తక్కువ (JPG, JPEG లేదా PNG) ఫైల్ అప్‌లోడ్ చేయండి",
    REMOVE: "తొలగించు",
    EMAIL: "ఇమెయిల్",
    PHONE_NUMBER: "ఫోన్ నంబర్",
    COUNTRY: "దేశం",
    STATE: "రాష్ట్రం",
    CITY: "నగరం",
    UPDATE: "నవీకరించు",
    PROFILE_UPDATED_SUCCESS: "ప్రొఫైల్ విజయవంతంగా నవీకరించబడింది",
    EX_JOHN_DEO: "ఉదా: జాన్ డియో",
    ENTER_EMAIL_PLACEHOLDER: "ఇమెయిల్ నమోదు చేయండి",
    ENTER_PHONE_PLACEHOLDER: "ఫోన్ నంబర్ నమోదు చేయండి",
    EX_INDIA: "ఉదా: భారత్",
    EX_KERALA: "ఉదా: కేరళ",
    EX_CALICUT: "ఉదా: కాలికట్",
    INVALID_PHONE: "చెల్లని ఫోన్ నంబర్",
    PHONE_NUMBER_EXISTS: "ఈ ఫోన్ నంబర్ ఇప్పటికే నమోదు చేయబడింది. దయచేసి వేరే నంబర్ ఉపయోగించండి.",
    LOADING: "లోడ్ అవుతోంది...",
    UNABLE_TO_LOAD_BOOKMARKS:
      "బుక్‌మార్క్‌లు లోడ్ చేయడం సాధ్యపడలేదు. దయచేసి తర్వాత మళ్ళీ ప్రయత్నించండి.",
    DELETE_ACCOUNT_CONFIRM: "మీరు ఖచ్చితంగా తొలగించాలనుకుంటున్నారా?",
    ACCOUNT_DELETED_SUCCESS: "ఖాతా విజయవంతంగా తొలగించబడింది",
    ACCOUNT_DELETION_FAILED: "ఖాతా తొలగింపు విఫలమైంది!",
    DELETE_ACCOUNT_WARNING:
      "హెచ్చరిక: మీ ఖాతాను తొలగించడం శాశ్వత చర్య మరియు దానిని రద్దు చేయలేము. మీ అన్ని డేటా కోల్పోవబడుతుంది మరియు పునరుద్ధరించలేము. దయచేసి జాగ్రత్తగా కొనసాగించండి.",
    DELETE: "తొలగించు",
    CANCEL: "రద్దు",
    CONFIRM: "నిర్ధారించండి",
    PHOTO: "ఫోటో",
    PHOTOGRAPHER: "ఫోటోగ్రాఫర్",
    PUBLISHED_ON: "ప్రచురించబడింది",
    MIN_READ: "నిమిషాల చదవడం",
    NEWS_DESK: "న్యూస్ డెస్క్",
    BOOKMARK: "బుక్మార్క్",
    LIKES: "లైక్‌లు",
    BLOCK_NOT_AVAILABLE: "బ్లాక్ డేటా అందుబాటులో లేదు",
    NO_ARTICLES: "ఈ ఉపవర్గానికి సంబంధించిన వ్యాసాలు ఏవీ కనుగొనబడలేదు",
    CATEGORY: "వర్గం",
    SUBCATEGORY: "ఉపవర్గం",
    NEXT_STORY: "తదుపరి కథనం",
    CLICK_TO_VIEW: "చూడడానికి క్లిక్ చేయండి",
  },
} as const;

/** Map common CMS block title strings (lowercase) to label keys for localization */
const BLOCK_TITLE_TO_KEY: Record<string, LabelKey> = {
  interview: "INTERVIEW",
  interviews: "INTERVIEWS_PAGE",
  "visual stories": "VISUAL_STORIES",
  "visual story": "VISUAL_STORIES",
  "photo stories": "PHOTO_STORIES",
  "photo story": "PHOTO_STORIES",
  "articles & periodicals": "ARTICLES_AND_PERIODICALS",
  "articles and periodicals": "ARTICLES_AND_PERIODICALS",
  articles: "ARTICLES_PAGE",
  "dont miss it": "DONT_MISS_IT",
  recommended: "RECOMMENDED",
  "recommended for you": "RECOMMENDED_FOR_YOU",
  "latest news": "LATEST_NEWS_PAGE",
  videos: "VIDEOS",
  "popular news": "POPULAR_NEWS",
  "recent videos": "RECENT_VIDEOS",
  global: "GLOBAL",
  "global progressive movements": "GLOBAL_PROGRESSIVE_MOVEMENTS",
  special: "WEBSITE_SPECIAL",
  specials: "SPECIALS",
  festivals: "FESTIVALS",
  festival: "FESTIVALS",
  "the latest": "THE_LATEST",
  podcasts: "PODCASTS",
  shorts: "SHORTS_PAGE",
  literature: "LITERATURE",
  exam: "EXAM",
  national: "NATIONAL",
  india: "INDIA",
  magazines: "MAGAZINES",
};

/**
 * Returns localized block title. Maps known English block titles from CMS to the current language.
 */
export function getBlockTitle(
  blockTitle: string | undefined,
  lang: AppLocale | string | undefined,
): string {
  if (!blockTitle || typeof blockTitle !== "string") return blockTitle ?? "";
  const key = BLOCK_TITLE_TO_KEY[blockTitle.trim().toLowerCase()];
  if (key) return getLabel(lang, key);
  return blockTitle;
}

/**
 * For nav/category: returns localized name. Use when name might be object (localized) or plain string (English).
 * If plain string and we have a matching label, returns translated label; otherwise returns name or getLocalizedString result.
 */
export function getDisplayName(
  name: string | { [key: string]: string } | null | undefined,
  locale: string,
): string {
  if (!name) return "";
  if (typeof name === "object") {
    const lang = (locale?.split("-")[0] || "en") as AppLocale;
    return name[lang] ?? name.en ?? name.te ?? Object.values(name)[0] ?? "";
  }
  const lang = (locale?.split("-")[0] || "en") as AppLocale;
  const key = BLOCK_TITLE_TO_KEY[name.trim().toLowerCase()];
  if (key) return getLabel(lang, key);
  return name;
}

/**
 * Returns the label for the given language. Falls back to English if key or lang is missing.
 */
export function getLabel(lang: AppLocale | string | undefined, key: LabelKey): string {
  const raw = (lang?.toString().trim().split(/[-_]/)[0] || "en").toLowerCase();
  const code: "en" | "te" = raw === "te" ? "te" : "en";
  const labels = code === "te" ? LABELS.te : LABELS.en;
  return (labels[key] as string) ?? (LABELS.en[key] as string) ?? key;
}
